import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  FileDown, FileSpreadsheet, FileText, Users, Package, Receipt, 
  Building2, FolderKanban, Truck, Wallet, Download, Calendar
} from 'lucide-react';
import api from '../lib/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const reportTypes = [
  { id: 'clients', name: 'Clients Report', icon: Users, description: 'Export all client data' },
  { id: 'invoices', name: 'Invoices Report', icon: Receipt, description: 'Export invoice records' },
  { id: 'expenses', name: 'Expenses Report', icon: Wallet, description: 'Export expense records' },
  { id: 'products', name: 'Products Report', icon: Package, description: 'Export product catalog' },
  { id: 'employees', name: 'Employees Report', icon: Users, description: 'Export employee data' },
  { id: 'projects', name: 'Projects Report', icon: FolderKanban, description: 'Export project data' },
  { id: 'suppliers', name: 'Suppliers Report', icon: Truck, description: 'Export supplier data' },
  { id: 'deals', name: 'Deals Report', icon: Building2, description: 'Export deals pipeline' },
];

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState('clients');
  const [exportFormat, setExportFormat] = useState('excel');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);

  useEffect(() => {
    if (selectedReport) {
      fetchPreviewData();
    }
  }, [selectedReport]);

  const fetchPreviewData = async () => {
    try {
      const response = await api.get(`/${selectedReport}`);
      setPreviewData(response.data.slice(0, 5)); // Preview first 5 records
    } catch (error) {
      console.error('Error fetching preview:', error);
      setPreviewData([]);
    }
  };

  const fetchReportData = async () => {
    try {
      const response = await api.get(`/${selectedReport}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching report data:', error);
      return [];
    }
  };

  const exportToExcel = async () => {
    setLoading(true);
    try {
      const data = await fetchReportData();
      
      // Clean data for export (remove internal fields)
      const cleanData = data.map(item => {
        const { _id, company_id, ...rest } = item;
        return rest;
      });

      const worksheet = XLSX.utils.json_to_sheet(cleanData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, selectedReport);
      
      // Generate file name with timestamp
      const fileName = `${selectedReport}_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = async () => {
    setLoading(true);
    try {
      const data = await fetchReportData();
      
      const doc = new jsPDF();
      const reportInfo = reportTypes.find(r => r.id === selectedReport);
      
      // Title
      doc.setFontSize(18);
      doc.setTextColor(15, 118, 110); // Teal color
      doc.text(reportInfo?.name || 'Report', 14, 22);
      
      // Date
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 30);
      doc.text(`Total Records: ${data.length}`, 14, 36);

      // Prepare table data
      if (data.length > 0) {
        const headers = Object.keys(data[0]).filter(key => !['_id', 'company_id', 'created_at', 'updated_at'].includes(key));
        const tableData = data.map(item => 
          headers.map(header => {
            const value = item[header];
            if (typeof value === 'object') return JSON.stringify(value);
            if (value === null || value === undefined) return '';
            return String(value);
          })
        );

        doc.autoTable({
          head: [headers.map(h => h.replace(/_/g, ' ').toUpperCase())],
          body: tableData,
          startY: 45,
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [15, 118, 110], textColor: 255 },
          alternateRowStyles: { fillColor: [245, 245, 245] },
        });
      }

      const fileName = `${selectedReport}_report_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('PDF export error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (exportFormat === 'excel') {
      exportToExcel();
    } else {
      exportToPDF();
    }
  };

  const getPreviewColumns = () => {
    if (previewData.length === 0) return [];
    return Object.keys(previewData[0]).filter(key => 
      !['_id', 'company_id', 'created_at', 'updated_at'].includes(key)
    ).slice(0, 5);
  };

  return (
    <div className="p-6 space-y-6" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Reports & Export</h1>
        <p className="text-slate-500 mt-1">Generate and export reports in Excel or PDF format</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Report Selection */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Select Report Type</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {reportTypes.map((report) => (
                <button
                  key={report.id}
                  onClick={() => setSelectedReport(report.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    selectedReport === report.id 
                      ? 'border-teal-500 bg-teal-50 text-teal-700' 
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  data-testid={`report-type-${report.id}`}
                >
                  <report.icon className={`h-5 w-5 ${selectedReport === report.id ? 'text-teal-600' : 'text-slate-400'}`} />
                  <div className="text-left">
                    <p className="font-medium text-sm">{report.name}</p>
                    <p className="text-xs text-slate-500">{report.description}</p>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Export Options & Preview */}
        <div className="lg:col-span-2 space-y-4">
          {/* Export Options */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Export Options</CardTitle>
              <CardDescription>Configure your export settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Export Format</Label>
                  <Select value={exportFormat} onValueChange={setExportFormat}>
                    <SelectTrigger data-testid="export-format-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excel">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="h-4 w-4 text-green-600" />
                          Excel (.xlsx)
                        </div>
                      </SelectItem>
                      <SelectItem value="pdf">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-red-600" />
                          PDF (.pdf)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date Range (Optional)</Label>
                  <div className="flex gap-2">
                    <Input 
                      type="date" 
                      value={dateFrom} 
                      onChange={(e) => setDateFrom(e.target.value)}
                      placeholder="From"
                    />
                    <Input 
                      type="date" 
                      value={dateTo} 
                      onChange={(e) => setDateTo(e.target.value)}
                      placeholder="To"
                    />
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleExport} 
                disabled={loading}
                className="w-full bg-teal-700 hover:bg-teal-800"
                data-testid="export-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Generating...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    Export {reportTypes.find(r => r.id === selectedReport)?.name}
                  </span>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Data Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                Data Preview
                <Badge variant="outline">{previewData.length} of many records</Badge>
              </CardTitle>
              <CardDescription>Preview of the first 5 records</CardDescription>
            </CardHeader>
            <CardContent>
              {previewData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        {getPreviewColumns().map((col) => (
                          <th key={col} className="text-left py-2 px-3 text-slate-600 font-medium">
                            {col.replace(/_/g, ' ').toUpperCase()}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((row, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          {getPreviewColumns().map((col) => (
                            <td key={col} className="py-2 px-3 text-slate-700">
                              {typeof row[col] === 'object' 
                                ? JSON.stringify(row[col]).slice(0, 30) + '...'
                                : String(row[col] || '-').slice(0, 30)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileDown className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                  <p>No data available for preview</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Reports;
